/**
 * Browse page data-fetching hooks.
 * Two projected SpacetimeDB views (nominations + sharable cards); the client
 * just dedupes the overlap between them.
 */
import { useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { Infer } from "spacetimedb";

import { ErrorHandler } from "@/web/lib/errors";
import { tableQueryOptions } from "@/web/lib/tanstack";
import { tables } from "@tycho/web-bindings";
import type CampaignBrowseNominationsRow from "@tycho/web-bindings/campaign_browse_nominations_table";
import type CampaignBrowseShareableCardsRow from "@tycho/web-bindings/campaign_browse_shareable_cards_table";
import type MyBusinessCardInterestsRow from "@tycho/web-bindings/my_business_card_interests_table";
import type MyNominationInterestsRow from "@tycho/web-bindings/my_nomination_interests_table";

import {
  filterBrowseResults,
  sortBrowseResults,
} from "@/web/routes/browse/-components/filterBrowseResults";
import {
  getBrowseResultKey,
  mapCampaignBrowseRows,
} from "@/web/routes/browse/-components/mapBrowseResults";
import type {
  BrowseResult,
  BrowseSearch,
  BrowseSort,
  DefaultStage,
} from "@/web/routes/browse/-components/types";

type CampaignBrowseNomination = Infer<typeof CampaignBrowseNominationsRow>;
type CampaignBrowseShareableCard = Infer<typeof CampaignBrowseShareableCardsRow>;
type MyBusinessCardInterest = Infer<typeof MyBusinessCardInterestsRow>;
type MyNominationInterest = Infer<typeof MyNominationInterestsRow>;

// ============================================================================
// useBrowseResults
// ============================================================================

/**
 * Search-independent core of the browse page: subscriptions, row mapping,
 * filtering, and sorting. Route-agnostic so other pages (e.g. the homepage's
 * featured rail) can render the same default-order results as `/browse`.
 */
export function useBrowseResults(search: BrowseSearch) {
  const nominationsQuery = useQuery(tableQueryOptions(tables.campaignBrowseNominations));
  const shareableCardsQuery = useQuery(tableQueryOptions(tables.campaignBrowseShareableCards));
  const myNominationInterestsQuery = useQuery(tableQueryOptions(tables.myNominationInterests));
  const myBusinessCardInterestsQuery = useQuery(tableQueryOptions(tables.myBusinessCardInterests));

  const nominations = (nominationsQuery.data ?? []) as CampaignBrowseNomination[];
  const shareableCards = (shareableCardsQuery.data ?? []) as CampaignBrowseShareableCard[];
  const myNominationInterests = (myNominationInterestsQuery.data ?? []) as MyNominationInterest[];
  const myBusinessCardInterests = (myBusinessCardInterestsQuery.data ??
    []) as MyBusinessCardInterest[];

  const myInterestsByBusinessId = useMemo(() => {
    const map = new Map<string, MyNominationInterest>();
    for (const interest of myNominationInterests) {
      map.set(interest.nominatedBusinessId, interest);
    }
    return map;
  }, [myNominationInterests]);

  // Map keyed by the shareable card's UUID (string form) so the browse card can
  // look up the calling user's existing EOI for that specific card. Mirrors the
  // `my_nomination_interests` map above; lives next to it because both flows
  // share the same row-write surface.
  const myInterestsByCardId = useMemo(() => {
    const map = new Map<string, MyBusinessCardInterest>();
    for (const interest of myBusinessCardInterests) {
      map.set(interest.cardId.toString(), interest);
    }
    return map;
  }, [myBusinessCardInterests]);

  const allResults = useMemo<BrowseResult[]>(
    () =>
      mapCampaignBrowseRows(
        nominations,
        shareableCards,
        myInterestsByBusinessId,
        myInterestsByCardId,
      ),
    [nominations, shareableCards, myInterestsByBusinessId, myInterestsByCardId],
  );

  const filtered = useMemo(() => filterBrowseResults(allResults, search), [allResults, search]);

  const results = useMemo(() => sortBrowseResults(filtered, search.sort), [filtered, search.sort]);

  const isPending = nominationsQuery.isPending || shareableCardsQuery.isPending;
  const isError = nominationsQuery.isError || shareableCardsQuery.isError;
  const isFetching = nominationsQuery.isFetching || shareableCardsQuery.isFetching;

  return { results, isPending, isFetching, isError };
}

// ============================================================================
// useCampaignBrowser
// ============================================================================

export function useCampaignBrowser() {
  const search = useSearch({ from: "/browse/" }) as BrowseSearch;
  return {
    ...useBrowseResults(search),
    hasNextPage: false,
    fetchNextPage: () => {},
    getResultKey: getBrowseResultKey,
  };
}

// ============================================================================
// useCampaignBrowserSearch
// ============================================================================

export function useCampaignBrowserSearch() {
  const search = useSearch({ from: "/browse/" }) as BrowseSearch;
  const navigate = useNavigate({ from: "/browse/" });

  function patch(update: Partial<BrowseSearch>) {
    navigate({ search: (prev: BrowseSearch) => ({ ...prev, ...update }) }).catch((e: unknown) =>
      ErrorHandler.default(e),
    );
  }

  function toggleCategory(category: string) {
    navigate({
      search: (prev: BrowseSearch) => {
        const current = prev.categories ?? [];
        const next = current.includes(category)
          ? current.filter((c) => c !== category)
          : [...current, category];
        return { ...prev, categories: next.length > 0 ? next : undefined };
      },
    }).catch((e: unknown) => ErrorHandler.default(e));
  }

  function removeCategory(category: string) {
    navigate({
      search: (prev: BrowseSearch) => {
        const next = (prev.categories ?? []).filter((c) => c !== category);
        return { ...prev, categories: next.length > 0 ? next : undefined };
      },
    }).catch((e: unknown) => ErrorHandler.default(e));
  }

  function toggleTag(tag: string) {
    navigate({
      search: (prev: BrowseSearch) => {
        const current = prev.tags ?? [];
        const next = current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag];
        return { ...prev, tags: next.length > 0 ? next : undefined };
      },
    }).catch((e: unknown) => ErrorHandler.default(e));
  }

  function removeTag(tag: string) {
    navigate({
      search: (prev: BrowseSearch) => {
        const next = (prev.tags ?? []).filter((t) => t !== tag);
        return { ...prev, tags: next.length > 0 ? next : undefined };
      },
    }).catch((e: unknown) => ErrorHandler.default(e));
  }

  function patchFullText(fullText: string) {
    patch({ fullText: fullText || undefined });
  }

  function clearFullText() {
    patch({ fullText: undefined });
  }

  function setStageTab(tab: "all" | DefaultStage) {
    patch({ stages: tab === "all" ? undefined : [tab] });
  }

  function setSort(sort: BrowseSort) {
    patch({ sort: sort === "recent" ? undefined : sort });
  }

  function clearSearch() {
    patch({ categories: undefined, tags: undefined, fullText: undefined });
  }

  const isDirty =
    (search.categories?.length ?? 0) > 0 ||
    (search.tags?.length ?? 0) > 0 ||
    Boolean(search.fullText);

  return {
    isDirty,
    search,
    toggleCategory,
    removeCategory,
    toggleTag,
    removeTag,
    patchFullText,
    clearFullText,
    setStageTab,
    setSort,
    clearSearch,
  };
}
